import React, { useState, useEffect } from 'react';
import { createApiUrl } from '../utils/apiConfig';
import { GRADE_LEVELS } from '../utils/gradeLevels';
import { isSubscriptionLessonSelectable } from '../utils/assetDisplayName';
import axios from 'axios';
import DragDropFileUpload from './DragDropFileUpload';
import styles from './QuizCreationModal.module.css';

const QuizCreationModal = ({ show, onHide, onQuizCreated, curriculumData, editingQuiz = null, isEditMode = false, subscriptionLessons }) => {
  const [activeTab, setActiveTab] = useState('ai-generate');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // AI Generation state
  const [aiFormData, setAiFormData] = useState({
    curriculumContent: '',
    subject: '',
    gradeLevel: '',
    difficulty: '',
    questionTypes: ['multiple-choice', 'essay'],
    questionCount: 5,
    estimatedDuration: 30,
    totalPoints: 100
  });
  const [uploadedFile, setUploadedFile] = useState(null);
  
  // Manual creation state
  const [manualFormData, setManualFormData] = useState({
    title: '',
    description: '',
    subject: '',
    gradeLevel: '',
    difficulty: '',
    estimatedDuration: 30,
    totalPoints: 100,
    questions: []
  });
  
  const [generatedQuiz, setGeneratedQuiz] = useState(null);
  const [_editingQuestion, setEditingQuestion] = useState(null);
  const [availableSubjects, setAvailableSubjects] = useState(['Black History']);
  const [newSubjectName, setNewSubjectName] = useState('');
  const [showAddSubjectInput, setShowAddSubjectInput] = useState(false);
  const [addingSubject, setAddingSubject] = useState(false);
  
  // Lesson selector state
  const [showLessonSelector, setShowLessonSelector] = useState(false);
  const [availableLessons, setAvailableLessons] = useState([]);
  const [loadingLessons, setLoadingLessons] = useState(false);

  // Fetch custom subjects from API (same as curriculum matcher)
  const fetchSubjects = async () => {
    try {
      const response = await axios.get(createApiUrl('/teacher/subjects'), {
        withCredentials: true
      });
      if (response.data.success) {
        setAvailableSubjects(response.data.data || ['Black History']);
      }
    } catch (error) {
      console.error('Error fetching subjects:', error);
      // Keep default subjects on error
      setAvailableSubjects(['Black History']);
    }
  };

  // Fetch lessons for selector
  const handleGetLessons = async () => {
    if (Array.isArray(subscriptionLessons)) {
      setAvailableLessons(subscriptionLessons);
      if (subscriptionLessons.length === 0) {
        setError('No lessons found. Please check your subscriptions.');
        return;
      }
      setShowLessonSelector(true);
      return;
    }

    setLoadingLessons(true);
    try {
      const response = await axios.get(createApiUrl('/lessons'), {
        headers: {
          'Accept': 'application/json'
        },
        withCredentials: true
      });
      const lessons = (Array.isArray(response.data)
        ? response.data
        : (Array.isArray(response.data?.lessons) ? response.data.lessons : [])
      ).filter(isSubscriptionLessonSelectable);
      setAvailableLessons(lessons);
      if (lessons.length === 0) {
        setError('No lessons found. Please check your subscriptions.');
        return;
      }
      setShowLessonSelector(true);
    } catch (error) {
      console.error('Error fetching lessons:', error);
      setError('Error fetching lessons. Please try again.');
    } finally {
      setLoadingLessons(false);
    }
  };

  // Handle lesson selection
  const handleSelectLesson = (lesson) => {
    const lessonDescription = lesson.description || '';
    handleAiFormChange('curriculumContent', lessonDescription);
    setShowLessonSelector(false);
  };

  // Close lesson selector
  const handleCloseLessonSelector = () => {
    setShowLessonSelector(false);
  };

  // Add new subject
  const handleAddSubject = async () => {
    if (!newSubjectName.trim()) {
      return;
    }
    
    setAddingSubject(true);
    try {
      const response = await axios.post(createApiUrl('/teacher/subjects'), {
        subjectName: newSubjectName.trim()
      }, {
        withCredentials: true
      });
      
      if (response.data.success) {
        // Refresh subjects list
        await fetchSubjects();
        // Set the newly added subject as selected based on active tab
        const subjectValue = newSubjectName.trim();
        if (activeTab === 'ai-generate') {
          handleAiFormChange('subject', subjectValue);
        } else if (activeTab === 'manual') {
          handleManualFormChange('subject', subjectValue);
        }
        // Clear input and hide add input
        setNewSubjectName('');
        setShowAddSubjectInput(false);
      }
    } catch (error) {
      console.error('Error adding subject:', error);
      alert('Failed to add subject. Please try again.');
    } finally {
      setAddingSubject(false);
    }
  };

  const handleFileSelect = (files) => {
    if (files && files.length > 0) {
      const file = files[0]; // Take first file for quiz generation
      setUploadedFile(file);
      // Only auto-populate textarea for text files, not PDF/Word
      if (file.type === 'text/plain' || file.name.endsWith('.txt')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const content = event.target.result;
          // Only auto-populate if textarea is empty
          if (!aiFormData.curriculumContent.trim()) {
            setAiFormData(prev => ({ ...prev, curriculumContent: content }));
          }
        };
        reader.readAsText(file);
      }
    }
  };

  const handleFileRemove = () => {
    setUploadedFile(null);
  };

  // Reset form function
  const resetForm = () => {
    const subject = curriculumData?.subject || '';
    const gradeLevel = curriculumData?.gradeLevel || '';
    setAiFormData({
      curriculumContent: curriculumData?.content || '',
      subject,
      gradeLevel,
      difficulty: '',
      questionTypes: ['multiple-choice', 'essay'],
      questionCount: 5,
      estimatedDuration: 30,
      totalPoints: 100
    });
    setManualFormData({
      title: '',
      description: '',
      subject,
      gradeLevel,
      difficulty: '',
      estimatedDuration: 30,
      totalPoints: 100,
      questions: []
    });
    setGeneratedQuiz(null);
    setEditingQuestion(null);
    setError(null);
    setSuccess(null);
    setActiveTab('ai-generate');
    setLoading(false);
    setUploadedFile(null);
    setShowAddSubjectInput(false);
    setNewSubjectName('');
    setShowLessonSelector(false);
    setAvailableLessons([]);
    setAvailableLessons([]);
  };

  // Initialize form with curriculum data
  useEffect(() => {
    if (show) {
      // Initialize subjects when modal opens
      fetchSubjects();
      // Reset add subject input when modal opens
      setShowAddSubjectInput(false);
      setNewSubjectName('');
      
      if (isEditMode && editingQuiz) {
        // Only populate the data for review tab display (not manual form)
        setGeneratedQuiz(editingQuiz);
        setActiveTab('review'); // Switch to review tab for editing
      } else {
        // Reset form when modal opens for creation (subject/gradeLevel from curriculumData in resetForm)
        resetForm();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- resetForm intentionally excluded
  }, [curriculumData, show, isEditMode, editingQuiz]);

  const handleAiFormChange = (field, value) => {
    setAiFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleManualFormChange = (field, value) => {
    setManualFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const generateQuiz = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const formData = new FormData();
      // Append text content if provided
      if (aiFormData.curriculumContent.trim()) {
        formData.append('curriculumContent', aiFormData.curriculumContent);
      }
      // Append file if uploaded
      if (uploadedFile) {
        formData.append('file', uploadedFile);
      }
      formData.append('subject', aiFormData.subject);
      formData.append('gradeLevel', aiFormData.gradeLevel);
      formData.append('difficulty', aiFormData.difficulty);
      formData.append('questionTypes', aiFormData.questionTypes.join(','));
      formData.append('questionCount', aiFormData.questionCount);

      const response = await axios.post(createApiUrl('/ai/generate-quiz'), formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true
      });

      if (response.data.success) {
        // The backend returns { success: true, data: { quiz: { ... } } }
        setGeneratedQuiz(response.data.data.quiz || response.data.data);
        setActiveTab('review');
        setSuccess('Quiz generated successfully!');
      } else {
        console.error('❌ Backend returned error:', response.data);
        setError(response.data.error || 'Failed to generate quiz');
      }
    } catch (err) {
      console.error('❌ Error details:', err.response?.data);
      setError(err.response?.data?.error || 'Failed to generate quiz. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const saveQuiz = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let quizData;
      
      if (isEditMode && editingQuiz) {
        // For editing, only send the fields that should be updated
        quizData = {
          title: generatedQuiz.title,
          description: generatedQuiz.description,
          subject: generatedQuiz.subject,
          gradeLevel: generatedQuiz.gradeLevel,
          difficulty: generatedQuiz.difficulty,
          estimatedDuration: generatedQuiz.estimatedDuration,
          totalPoints: generatedQuiz.totalPoints,
          questions: generatedQuiz.questions || []
        };
      } else {
        // For creating, use the appropriate form data
        const sourceData = activeTab === 'review' ? generatedQuiz : manualFormData;
        quizData = {
          ...sourceData,
          gradeLevel: sourceData.gradeLevel
        };
      }
      
      let response;
      if (isEditMode && editingQuiz) {
        // Update existing quiz
        const updateUrl = createApiUrl(`/quiz/${editingQuiz._id}`);
        response = await axios.put(updateUrl, quizData, {
          withCredentials: true
        });
      } else {
        // Create new quiz
        const createUrl = createApiUrl('/quiz');
        response = await axios.post(createUrl, quizData, {
          withCredentials: true
        });
      }

      if (response.data.success) {
        setSuccess(isEditMode ? 'Quiz updated successfully!' : 'Quiz saved successfully!');
        if (onQuizCreated) {
          onQuizCreated(response.data.data?.quiz || response.data.quiz);
        }
        
        // Reset form after successful save (only for creation, not editing)
        if (!isEditMode) {
          setTimeout(() => {
            resetForm();
            onHide();
          }, 1500);
        } else {
          setTimeout(() => {
            onHide();
          }, 1500);
        }
      }
    } catch (err) {
      setError(isEditMode ? 'Failed to update quiz. Please try again.' : 'Failed to save quiz. Please try again.');
      console.error('❌ [SAVE QUIZ] Error saving quiz:', err);
      console.error('❌ [SAVE QUIZ] Error details:', {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        config: {
          url: err.config?.url,
          method: err.config?.method,
          baseURL: err.config?.baseURL
        }
      });
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;
  
  return (
    <div className={styles.modal}>
      <div className={styles.modalDialog}>
        <div className={styles.modalContent}>
          <div className={styles.modalHeader}>
            <h5 className={styles.modalTitle}>{isEditMode ? 'Edit Quiz' : 'Create New Quiz'}</h5>
            <button type="button" className={styles.closeBtn} onClick={() => {
              if (!isEditMode) {
                resetForm();
              } else {
                setShowAddSubjectInput(false);
                setNewSubjectName('');
                setShowLessonSelector(false);
              }
              onHide();
            }}>×</button>
          </div>
          <div className={styles.modalBody}>
            {error && <div className={`${styles.alert} ${styles.alertDanger}`}>{error}</div>}
            {success && <div className={`${styles.alert} ${styles.alertSuccess}`}>{success}</div>}
        
            <div className={styles.tabsContainer}>
              <div className={styles.tabsNav}>
                <button 
                  className={`${styles.tabButton} ${activeTab === 'ai-generate' ? styles.active : ''}`}
                  onClick={() => {
                    setActiveTab('ai-generate');
                    setShowAddSubjectInput(false);
                    setNewSubjectName('');
                  }}
                >
                  AI Generate
                </button>
                <button 
                  className={`${styles.tabButton} ${activeTab === 'manual' ? styles.active : ''}`}
                  onClick={() => {
                    setActiveTab('manual');
                    setShowAddSubjectInput(false);
                    setNewSubjectName('');
                  }}
                >
                  Manual Create
                </button>
                <button 
                  className={`${styles.tabButton} ${activeTab === 'review' ? styles.active : ''}`}
                  onClick={() => {
                    setActiveTab('review');
                    setShowAddSubjectInput(false);
                    setNewSubjectName('');
                  }}
                >
                  Review & Save
                </button>
              </div>
              <div className={styles.tabContent}>
                {activeTab === 'ai-generate' && (
                  <div>
                    <form className={styles.form}>
                      <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                          <label className={styles.formLabel}>Subject</label>
                          <select 
                            className={styles.formSelect}
                            value={aiFormData.subject}
                            onChange={(e) => {
                              if (e.target.value === '__add_new__') {
                                setShowAddSubjectInput(true);
                                handleAiFormChange('subject', '');
                              } else {
                                setShowAddSubjectInput(false);
                                setNewSubjectName('');
                                handleAiFormChange('subject', e.target.value);
                              }
                            }}
                          >
                            <option value="">Select one</option>
                            {availableSubjects.map(subject => (
                              <option key={subject} value={subject}>{subject}</option>
                            ))}
                            <option value="__add_new__">+ Add New Subject</option>
                          </select>
                          {showAddSubjectInput && (
                            <div style={{ marginTop: '8px' }}>
                              <input
                                type="text"
                                className={styles.formSelect}
                                placeholder="Enter new subject name"
                                value={newSubjectName}
                                onChange={(e) => setNewSubjectName(e.target.value)}
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddSubject();
                                  }
                                }}
                                style={{ width: '100%', marginBottom: '8px' }}
                              />
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                  type="button"
                                  className="btn btn-success btn-sm"
                                  onClick={handleAddSubject}
                                  disabled={addingSubject || !newSubjectName.trim()}
                                  style={{ fontSize: '0.875rem', padding: '0.25rem 0.75rem' }}
                                >
                                  {addingSubject ? 'Saving...' : 'Save'}
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-secondary btn-sm"
                                  onClick={() => {
                                    setShowAddSubjectInput(false);
                                    setNewSubjectName('');
                                  }}
                                  style={{ fontSize: '0.875rem', padding: '0.25rem 0.75rem' }}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className={styles.formGroup}>
                          <label className={styles.formLabel}>Grade Level</label>
                          <select
                            className={styles.formSelect}
                            value={aiFormData.gradeLevel}
                            onChange={(e) => handleAiFormChange('gradeLevel', e.target.value)}
                          >
                            <option value="">Choose One</option>
                            {GRADE_LEVELS.map(grade => (
                              <option key={grade.value} value={grade.value}>{grade.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                          <label className={styles.formLabel}>Difficulty</label>
                          <select
                            className={styles.formSelect}
                            value={aiFormData.difficulty}
                            onChange={(e) => handleAiFormChange('difficulty', e.target.value)}
                          >
                            <option value="">Choose One</option>
                            <option value="beginner">Beginner</option>
                            <option value="intermediate">Intermediate</option>
                            <option value="advanced">Advanced</option>
                          </select>
                        </div>
                        <div className={styles.formGroup}>
                          <label className={styles.formLabel}>Number of Questions</label>
                          <select
                            className={styles.formSelect}
                            value={aiFormData.questionCount}
                            onChange={(e) => handleAiFormChange('questionCount', parseInt(e.target.value))}
                          >
                            <option value="">Choose One</option>
                            <option value="5">5 Questions</option>
                            <option value="10">10 Questions</option>
                            <option value="15">15 Questions</option>
                            <option value="20">20 Questions</option>
                          </select>
                        </div>
                      </div>

                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Curriculum Content</label>
                        <div style={{ marginBottom: '10px' }}>
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleGetLessons}
                            disabled={loadingLessons}
                            style={{ 
                              fontSize: '0.9rem', 
                              padding: '0.5rem 1rem',
                              marginBottom: '10px',
                              width: '100%',
                              maxWidth: '200px'
                            }}
                          >
                            {loadingLessons ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Loading...
                              </>
                            ) : (
                              '📚 Get Lessons'
                            )}
                          </button>
                        </div>
                        <div className="mb-2">
                          <textarea
                            className={styles.formTextarea}
                            value={aiFormData.curriculumContent}
                            onChange={(e) => handleAiFormChange('curriculumContent', e.target.value)}
                            placeholder="Enter curriculum content or paste text here... Or click 'Get Lessons' above to select from available lessons."
                            rows="6"
                          />
                        </div>
                        <div className="mb-2">
                          <label className="form-label small text-muted">Or upload a file:</label>
                          <DragDropFileUpload
                            onFileSelected={handleFileSelect}
                            onFileRemoved={handleFileRemove}
                            selectedFile={uploadedFile}
                            accept=".pdf,.doc,.docx,.txt"
                            multiple={false}
                            maxFileSize={10 * 1024 * 1024} // 10MB
                            supportedTypes={['.pdf', '.doc', '.docx', '.txt']}
                            placeholder="Click to upload file or drag and drop"
                            description="Supports: PDF, Word (.doc, .docx), Text (.txt)"
                            className="mb-2"
                          />
                        </div>
                      </div>

                      <div className={styles.buttonContainer}>
                        <button
                          type="button"
                          className={`${styles.button} ${styles.buttonPrimary}`}
                          onClick={generateQuiz}
                          disabled={loading || (!aiFormData.curriculumContent.trim() && !uploadedFile)}
                        >
                          {loading ? (
                            <>
                              <span className={styles.spinner}></span> Generating...
                            </>
                          ) : (
                            'Generate Quiz'
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {activeTab === 'manual' && (
                  <div>
                    <form className={styles.form}>
                      <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                          <label className={styles.formLabel}>Quiz Title</label>
                          <input
                            type="text"
                            className={styles.formInput}
                            value={manualFormData.title}
                            onChange={(e) => handleManualFormChange('title', e.target.value)}
                            placeholder="Enter quiz title"
                          />
                        </div>
                        <div className={styles.formGroup}>
                          <label className={styles.formLabel}>Subject</label>
                          <select 
                            className={styles.formSelect}
                            value={manualFormData.subject}
                            onChange={(e) => {
                              if (e.target.value === '__add_new__') {
                                setShowAddSubjectInput(true);
                                handleManualFormChange('subject', '');
                              } else {
                                setShowAddSubjectInput(false);
                                setNewSubjectName('');
                                handleManualFormChange('subject', e.target.value);
                              }
                            }}
                          >
                            <option value="">Select one</option>
                            {availableSubjects.map(subject => (
                              <option key={subject} value={subject}>{subject}</option>
                            ))}
                            <option value="__add_new__">+ Add New Subject</option>
                          </select>
                          {showAddSubjectInput && (
                            <div style={{ marginTop: '8px' }}>
                              <input
                                type="text"
                                className={styles.formSelect}
                                placeholder="Enter new subject name"
                                value={newSubjectName}
                                onChange={(e) => setNewSubjectName(e.target.value)}
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddSubject();
                                  }
                                }}
                                style={{ width: '100%', marginBottom: '8px' }}
                              />
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                  type="button"
                                  className="btn btn-success btn-sm"
                                  onClick={handleAddSubject}
                                  disabled={addingSubject || !newSubjectName.trim()}
                                  style={{ fontSize: '0.875rem', padding: '0.25rem 0.75rem' }}
                                >
                                  {addingSubject ? 'Saving...' : 'Save'}
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-secondary btn-sm"
                                  onClick={() => {
                                    setShowAddSubjectInput(false);
                                    setNewSubjectName('');
                                  }}
                                  style={{ fontSize: '0.875rem', padding: '0.25rem 0.75rem' }}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Description</label>
                        <textarea
                          className={styles.formTextarea}
                          value={manualFormData.description}
                          onChange={(e) => handleManualFormChange('description', e.target.value)}
                          placeholder="Enter quiz description"
                          rows="3"
                        />
                      </div>

                      <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                          <label className={styles.formLabel}>Grade Level</label>
                          <select
                            className={styles.formSelect}
                            value={manualFormData.gradeLevel}
                            onChange={(e) => handleManualFormChange('gradeLevel', e.target.value)}
                          >
                            <option value="">Choose One</option>
                            {GRADE_LEVELS.map(grade => (
                              <option key={grade.value} value={grade.value}>{grade.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className={styles.formGroup}>
                          <label className={styles.formLabel}>Difficulty</label>
                          <select
                            className={styles.formSelect}
                            value={manualFormData.difficulty}
                            onChange={(e) => handleManualFormChange('difficulty', e.target.value)}
                          >
                            <option value="">Choose One</option>
                            <option value="beginner">Beginner</option>
                            <option value="intermediate">Intermediate</option>
                            <option value="advanced">Advanced</option>
                          </select>
                        </div>
                      </div>

                      <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                          <label className={styles.formLabel}>Estimated Duration (minutes)</label>
                          <input
                            type="number"
                            className={styles.formInput}
                            value={manualFormData.estimatedDuration}
                            onChange={(e) => handleManualFormChange('estimatedDuration', parseInt(e.target.value) || 30)}
                            placeholder="30"
                            min="1"
                          />
                        </div>
                        <div className={styles.formGroup}>
                          <label className={styles.formLabel}>Point Distribution</label>
                          <div className={styles.pointDistributionNote}>
                            <p>Points are automatically distributed to total 100:</p>
                            <ul>
                              <li>Essay questions: 20 points each</li>
                              <li>Other questions: Equal distribution of remaining points</li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Questions</label>
                        <div className={styles.questionsContainer}>
                          {manualFormData.questions && manualFormData.questions.length > 0 ? (
                            <div className={styles.questionsList}>
                              {manualFormData.questions.map((question, index) => (
                                <div key={index} className={styles.questionItem}>
                                  <div className={styles.questionHeader}>
                                    <span className={styles.questionNumber}>Q{index + 1}</span>
                                    <span className={styles.questionType}>({question.type})</span>
                                    <span className={styles.questionPoints}>{question.points} pts</span>
                                  </div>
                                  <p className={styles.questionText}>{question.question}</p>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className={styles.noQuestions}>No questions added yet. Questions can be added after creating the quiz.</p>
                          )}
                        </div>
                      </div>
                    </form>
                  </div>
                )}

                {activeTab === 'review' && generatedQuiz && (
                  <div>
                    <h4>{isEditMode ? 'Edit Quiz' : 'Generated Quiz Preview'}</h4>
                    
                    {/* Debug info */}
                    {isEditMode && (
                      <div style={{ background: '#f8f9fa', padding: '10px', margin: '10px 0', border: '1px solid #dee2e6', borderRadius: '4px' }}>
                        <strong>Debug Info:</strong>
                        <br />isEditMode: {isEditMode ? 'true' : 'false'}
                        <br />editingQuiz: {editingQuiz ? 'present' : 'null'}
                        <br />generatedQuiz: {generatedQuiz ? 'present' : 'null'}
                        <br />generatedQuiz.title: {generatedQuiz?.title || 'empty'}
                        <br />Data source: generatedQuiz (not manualFormData)
                      </div>
                    )}
                    
                    <div className={styles.quizPreview}>
                      {isEditMode ? (
                        // Edit mode - show editable fields using generatedQuiz data
                        <form className={styles.form}>
                          <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Quiz Title</label>
                            <input
                              type="text"
                              className={styles.formInput}
                              value={generatedQuiz.title || ''}
                              onChange={(e) => setGeneratedQuiz(prev => ({ ...prev, title: e.target.value }))}
                              placeholder="Enter quiz title"
                            />
                          </div>
                          
                          <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Description</label>
                            <textarea
                              className={styles.formTextarea}
                              value={generatedQuiz.description || ''}
                              onChange={(e) => setGeneratedQuiz(prev => ({ ...prev, description: e.target.value }))}
                              placeholder="Enter quiz description"
                              rows="3"
                            />
                          </div>

                          <div className={styles.formRow}>
                            <div className={styles.formGroup}>
                              <label className={styles.formLabel}>Subject</label>
                              <select
                                className={styles.formSelect}
                                value={generatedQuiz.subject || ''}
                                onChange={(e) => setGeneratedQuiz(prev => ({ ...prev, subject: e.target.value }))}
                              >
                                <option value="">Choose One</option>
                                <option value="Mathematics">Mathematics</option>
                                <option value="Science">Science</option>
                                <option value="History">History</option>
                                <option value="English">English</option>
                                <option value="Geography">Geography</option>
                                <option value="Social Studies">Social Studies</option>
                                <option value="Art">Art</option>
                                <option value="Music">Music</option>
                                <option value="Physical Education">Physical Education</option>
                                <option value="Other">Other</option>
                              </select>
                            </div>
                            <div className={styles.formGroup}>
                              <label className={styles.formLabel}>Grade Level</label>
                              <select
                                className={styles.formSelect}
                                value={generatedQuiz.gradeLevel || ''}
                                onChange={(e) => setGeneratedQuiz(prev => ({ ...prev, gradeLevel: e.target.value }))}
                              >
                                <option value="">Choose One</option>
                                {GRADE_LEVELS.map(grade => (
                                  <option key={grade.value} value={grade.value}>{grade.label}</option>
                                ))}
                              </select>
                            </div>
                          </div>

                          <div className={styles.formRow}>
                            <div className={styles.formGroup}>
                              <label className={styles.formLabel}>Difficulty</label>
                              <select
                                className={styles.formSelect}
                                value={generatedQuiz.difficulty || ''}
                                onChange={(e) => setGeneratedQuiz(prev => ({ ...prev, difficulty: e.target.value }))}
                              >
                                <option value="">Choose One</option>
                                <option value="beginner">Beginner</option>
                                <option value="intermediate">Intermediate</option>
                                <option value="advanced">Advanced</option>
                              </select>
                            </div>
                            <div className={styles.formGroup}>
                              <label className={styles.formLabel}>Estimated Duration (minutes)</label>
                              <input
                                type="number"
                                className={styles.formInput}
                                value={generatedQuiz.estimatedDuration || 30}
                                onChange={(e) => setGeneratedQuiz(prev => ({ ...prev, estimatedDuration: parseInt(e.target.value) || 30 }))}
                                placeholder="30"
                                min="1"
                              />
                            </div>
                          </div>

                          <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Total Points</label>
                            <input
                              type="number"
                              className={styles.formInput}
                              value={generatedQuiz.totalPoints || 100}
                              onChange={(e) => setGeneratedQuiz(prev => ({ ...prev, totalPoints: parseInt(e.target.value) || 100 }))}
                              placeholder="100"
                              min="1"
                              style={{ maxWidth: '200px' }}
                            />
                          </div>
                        </form>
                      ) : (
                        // Preview mode - show read-only data
                        <>
                          <h5>{generatedQuiz.title}</h5>
                          <p>{generatedQuiz.description}</p>
                          <p><strong>Subject:</strong> {generatedQuiz.subject}</p>
                          <p><strong>Grade Level:</strong> {generatedQuiz.gradeLevel}</p>
                          <p><strong>Difficulty:</strong> {generatedQuiz.difficulty}</p>
                          <p><strong>Questions:</strong> {generatedQuiz.questions?.length || 0}</p>
                          <p><strong>Total Points:</strong> {generatedQuiz.totalPoints}</p>
                        </>
                      )}
                    </div>
                    
                    {/* Display Questions */}
                    {generatedQuiz.questions && generatedQuiz.questions.length > 0 && (
                      <div className={styles.questionsPreview}>
                        <h5>Questions:</h5>
                        {generatedQuiz.questions.map((question, index) => (
                          <div key={index} className={styles.questionItem}>
                            <h6>Question {index + 1} ({question.type}) - {question.points} points</h6>
                            <p><strong>{question.question}</strong></p>
                            
                            {question.type === 'multiple-choice' && question.options && (
                              <ul>
                                {question.options.map((option, optIndex) => (
                                  <li key={optIndex} className={optIndex === question.correctAnswer ? styles.correctAnswer : ''}>
                                    {option} {optIndex === question.correctAnswer ? '✓' : ''}
                                  </li>
                                ))}
                              </ul>
                            )}
                            
                            {question.type === 'true-false' && (
                              <p>Answer: {question.correctAnswer ? 'True' : 'False'}</p>
                            )}
                            
                            {question.type === 'fill-blank' && (
                              <p>Correct Answer: {question.correctText}</p>
                            )}
                            
                            {question.explanation && (
                              <p><em>Explanation: {question.explanation}</em></p>
                            )}
                            
                            {question.blackHistoryConnection && (
                              <div className={styles.blackHistoryConnection}>
                                <strong>Black History Connection:</strong>
                                <p><strong>Figure:</strong> {question.blackHistoryConnection.figure}</p>
                                <p><strong>Context:</strong> {question.blackHistoryConnection.context}</p>
                                <p><strong>Significance:</strong> {question.blackHistoryConnection.significance}</p>
                              </div>
                            )}
                            
                            {question.hints && question.hints.length > 0 && (
                              <div className={styles.hints}>
                                <strong>Hints:</strong>
                                <ul>
                                  {question.hints.map((hint, hintIndex) => (
                                    <li key={hintIndex}>{hint}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className={styles.modalFooter}>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonSecondary}`}
              onClick={() => {
                if (!isEditMode) {
                  resetForm();
                } else {
                  setShowLessonSelector(false);
                  setShowAddSubjectInput(false);
                  setNewSubjectName('');
                }
                onHide();
              }}
            >
              Cancel
            </button>
            {!isEditMode && (
              <button
                type="button"
                className={`${styles.button} ${styles.buttonSecondary}`}
                onClick={() => {
                  resetForm();
                  setShowLessonSelector(false);
                  setAvailableLessons([]);
                  setActiveTab('ai-generate');
                }}
                style={{ marginLeft: '0.5rem' }}
              >
                Reset Form
              </button>
            )}
            {(activeTab === 'review' || activeTab === 'manual') && (
              <button
                type="button"
                className={`${styles.button} ${styles.buttonPrimary}`}
                onClick={saveQuiz}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className={styles.spinner}></span> {isEditMode ? 'Updating...' : 'Saving...'}
                  </>
                ) : (
                  isEditMode ? 'Update Quiz' : 'Save Quiz'
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Lesson Selector Modal */}
      {showLessonSelector && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={handleCloseLessonSelector}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '20px',
              maxWidth: '600px',
              maxHeight: '80vh',
              width: '90%',
              overflow: 'auto',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0 }}>Select a Lesson</h3>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseLessonSelector}
                aria-label="Close"
              ></button>
            </div>
            
            <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {loadingLessons ? (
                <div className="text-center py-4">
                  <p>Loading lessons...</p>
                </div>
              ) : availableLessons.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted">No lessons available. Please check your subscriptions.</p>
                </div>
              ) : (
                <div>
                  {availableLessons.map((lesson) => (
                    <div
                      key={lesson._id}
                      style={{
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        padding: '15px',
                        marginBottom: '10px',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s'
                      }}
                      onClick={() => handleSelectLesson(lesson)}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                    >
                      <h5 style={{ margin: '0 0 8px 0', color: '#333' }}>{lesson.name || 'Untitled Lesson'}</h5>
                      <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
                        {lesson.description || 'No description available'}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCloseLessonSelector}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuizCreationModal;